import { useSelectedFacility, useOrgFacilitiesQuery } from "@/app/FacilityContext"
import { useMyOrg } from "@/hooks/use-my-org"
import type { FacilityListItem } from "@/libs/facilityApi"

/**
 * useResolvedFacility — the single facility resolver every migrated workspace
 * adopts (replacing the per-component getMyFacility fetch). It resolves the
 * FacilityContext-selected id against the org's facility-list query and returns
 * the full facility object plus a loading flag.
 *
 *   - no selection            → { facility: null, facilityId: null }
 *   - selected, list loading  → { isLoading: true, facility: null, facilityId: null }
 *   - selected, in list       → the matching facility object
 *   - selected, NOT in list   → { facility: null, facilityId: null } — the id is
 *     foreign/unknown and must NOT be exposed (see below)
 *
 * SECURITY (#883 cross-org isolation): a tampered/stale
 * `localStorage.bw_selected_facility` may hold a facility belonging to a
 * DIFFERENT org. `useFacilitySync` only clears it in a post-commit effect, so
 * this resolver is the LAST line of defense before a workspace fires a query
 * against `facilityId`. Every migrated workspace gates its read on a truthy
 * `facilityId`, so the resolver must expose the selected id ONLY once it has been
 * confirmed to be owned by the current org's facility list. It therefore returns
 * `facilityId: null` in BOTH unverified states — while the list is still loading
 * (we cannot yet know the id is owned) and once it has loaded and the id is not
 * in it (foreign/unknown). No downstream read can ever target another org's
 * facility; the Layer-3 gate shows NoFacilitySelected and the sync effect
 * auto-selects an owned facility.
 *
 * The "no facility" case is owned by the Layer-3 gate, so a workspace that uses
 * this hook can assume it is only rendered once a facility exists — it just shows
 * its own loading affordance during the brief null window.
 */

export interface ResolvedFacility {
  facility: FacilityListItem | null
  facilityId: string | null
  facilities: FacilityListItem[]
  isLoading: boolean
}

export function useResolvedFacility(): ResolvedFacility {
  const { facilityId } = useSelectedFacility()
  const orgView = useMyOrg()
  const orgId = orgView.org?.org_id

  const { data: facilities, isLoading: facilitiesLoading } =
    useOrgFacilitiesQuery(orgId)

  // Nothing selected — the gate keeps operational pages off-screen, so callers
  // never have to render an error for this; they just see null.
  if (!facilityId) {
    return { facility: null, facilityId: null, facilities: [], isLoading: false }
  }

  // The org / facility list is still resolving — we cannot yet KNOW whether the
  // selected id is owned. Report loading and expose NO id: a workspace gates its
  // read on a truthy facilityId, so withholding the unverified id prevents any
  // fetch against a not-yet-confirmed (possibly foreign) facility.
  if (facilitiesLoading || orgView.kind === "loading") {
    return { facility: null, facilityId: null, facilities: [], isLoading: true }
  }

  const facility =
    facilities?.find((f) => f.facility_id === facilityId) ?? null

  // List loaded but the selected id is NOT owned (foreign / stale). Expose NO id
  // so no downstream query can target another org's facility — the sync effect
  // clears the selection and auto-selects an owned one on the next commit.
  if (facility === null) {
    return {
      facility: null,
      facilityId: null,
      facilities: facilities ?? [],
      isLoading: false,
    }
  }

  return { facility, facilityId, facilities: facilities ?? [], isLoading: false }
}
